import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from '../common/modal';
import VerticalMultiSelect, { VerticalOption } from './vertical-multi-select';
import { TagGroupMultiSelect, DomainSingleSelect, ClientSingleSelectWithSearch } from './filter-controls';
import { use2026TagGroups } from '../../hooks/useTagGroups';
import { useDomains, useVerticals } from '../../hooks/useClients';

export interface CollectionsFilterValues {
  tagGroupIds: number[];
  verticalIds: number[];
  domainId: number | null;
  clientId: number | null;
}

interface CollectionsFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (values: CollectionsFilterValues) => void;
  initialValues: CollectionsFilterValues;
}

const CollectionsFilterModal: React.FC<CollectionsFilterModalProps> = ({ isOpen, onClose, onApply, initialValues }) => {
  const [values, setValues] = useState<CollectionsFilterValues>(initialValues);

  const { data: tagGroupsData, isLoading: isLoadingTagGroups } = use2026TagGroups();
  const topicTagGroups = useMemo(
    () => (tagGroupsData ?? []).filter((g) => g.tag_group_type === 'topic'),
    [tagGroupsData],
  );
  const { data: domains } = useDomains();
  const { data: verticals, isLoading: isLoadingVerticals } = useVerticals();

  const verticalOptions: VerticalOption[] = useMemo(
    () => (verticals ?? []).map((v) => ({ id: v.id, key: v.key, name: v.name, description: v.description })),
    [verticals],
  );
  const domainOptions = useMemo(() => (domains ?? []).map((d) => ({ id: d.id, name: d.name })), [domains]);

  useEffect(() => {
    if (isOpen) {
      setValues(initialValues);
    }
  }, [isOpen, initialValues]);

  const update = useCallback(<K extends keyof CollectionsFilterValues>(key: K, v: CollectionsFilterValues[K]) => {
    setValues((prev) => ({ ...prev, [key]: v }));
  }, []);

  const handleApply = useCallback(() => {
    onApply(values);
    onClose();
  }, [values, onApply, onClose]);

  const handleClear = useCallback(() => {
    setValues({
      tagGroupIds: [],
      verticalIds: [],
      domainId: null,
      clientId: null,
    });
  }, []);

  const hasFilters = useMemo(
    () =>
      values.tagGroupIds.length > 0 ||
      values.verticalIds.length > 0 ||
      values.domainId != null ||
      values.clientId != null,
    [values],
  );

  if (!isOpen) return null;

  return (
    <Modal
      isModel={true}
      InnerComponent={
        <div
          className="inline-block w-full max-w-md transform overflow-visible rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:align-middle"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Filter Collections</h3>
              <button
                type="button"
                className="text-blue-primary text-sm font-semibold hover:underline"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
          <div className="space-y-4 px-6 py-5">
            <div>
              <TagGroupMultiSelect
                label="Topic"
                tagGroups={topicTagGroups}
                selectedIds={values.tagGroupIds}
                onChange={(ids) => update('tagGroupIds', ids)}
                placeholder="Select topics"
                isLoading={isLoadingTagGroups}
              />
              <p className="mt-1 text-xs text-gray-500">Filter by topics tagged on the collection.</p>
            </div>
            <div>
              <DomainSingleSelect
                label="Domain"
                domains={domainOptions}
                selectedId={values.domainId}
                onChange={(id) => update('domainId', id)}
                placeholder="All domains"
              />
              <p className="mt-1 text-xs text-gray-500">Filter by domain associated with the collection.</p>
            </div>
            <div>
              <VerticalMultiSelect
                label="Vertical"
                verticals={verticalOptions}
                selectedVerticalIds={values.verticalIds}
                onChange={(ids) => update('verticalIds', ids)}
                placeholder="Select verticals"
                isLoading={isLoadingVerticals}
              />
              <p className="mt-1 text-xs text-gray-500">Filter by vertical associated with the collection.</p>
            </div>
            <div>
              <ClientSingleSelectWithSearch
                label="Client"
                selectedClientId={values.clientId}
                onChange={(id) => update('clientId', id)}
                placeholder="All clients"
              />
              <p className="mt-1 text-xs text-gray-500">Filter by client associated with the collection.</p>
            </div>
          </div>
          <div className="flex justify-between gap-2 border-t border-gray-100 px-6 py-4">
            <button
              type="button"
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              onClick={handleClear}
              disabled={!hasFilters}
            >
              Clear
            </button>
            <button
              type="button"
              className="bg-blue-primary rounded-md px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
              onClick={handleApply}
            >
              Apply Filters
            </button>
          </div>
        </div>
      }
    />
  );
};

export default CollectionsFilterModal;
